<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .custom-image-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    $gap: utils.in_rem(8px);
    gap: $gap;
    align-self: stretch;
    width: 100%;

    .file-input {
      display: none;
    }

    .custom-image-setting {
      display: flex;
      align-items: center;
      gap: utils.in_rem(8px);
      justify-content: space-between;
      width: 100%;

      .image-wrapper {
        display: flex;
        align-items: center;
        gap: utils.in_rem(8px);
        max-width: calc(100% - $gap - utils.in_rem(100px));

        .image {
          height: utils.in_rem(90px);
          width: utils.in_rem(160px);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: utils.in_rem(8px);
          @include colors.background-color(interfaceSwGrey_10);
          flex-shrink: 0;

          &.skeleton {
            @include mixins.skeleton(
              (
                opacity: 0.5,
              )
            );
          }

          > img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: inherit;
          }
        }

        .image-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;

          .image-name {
            @include mixins.h3();
            @include colors.color(textBlack);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 100%;
          }

          .image-uploaded {
            @include mixins.body3-tooltip-and-caption();
            @include colors.color(textGrey);
          }
        }
      }
    }

    --text-grey: #{colors.get(textGrey)};
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Icon from "@sourcewhale/shared/svelte-design-system/components/atoms/Icon.svelte";
  import InlineNotification from "@sourcewhale/shared/svelte-design-system/components/atoms/InlineNotification.svelte";
  import {
    getTimezone,
    localeString,
    showToast,
  } from "@sourcewhale/shared/src/assets/js/src/general/utils";
  import {
    teamNotetakerSettingsStore,
    teamStore,
  } from "../../../../api-stores/meetings-store";
  import { customizeNotetakerBot } from "../../../../api/notetaker";
  import { ToastError } from "@sourcewhale/shared/src/svelte/api/sw-toast-error";

  let fileInput;
  let uploading = false;
  let errorText = "";

  const clearFileInput = () => {
    if (fileInput) {
      fileInput.value = "";
    }
  };

  $: ({
    fileName = "Upload an image",
    uploadedAt,
    imageUrl,
  } = $teamNotetakerSettingsStore?.customBotImage || {});

  $: updatedText = uploadedAt
    ? `Updated ${localeString(new Date(uploadedAt * 1000), {
        month: "long",
        day: "2-digit",
        weekday: "short",
        timezone: getTimezone(),
      })}`
    : "";

  const validateFile = (file) => {
    if (!["image/jpeg", "image/jpg"].includes(file.type)) {
      return { valid: false, errorMsg: "Please upload a JPG/JPEG file only." };
    }

    if (file.size > 2097152) {
      return { valid: false, errorMsg: "File size must be 2MB or less." };
    }

    return { valid: true };
  };

  const handleFileUpload = async (file) => {
    uploading = true;

    try {
      const fileReader = new FileReader();

      fileReader.onload = async (e) => {
        try {
          // Get base64 string without the data URL prefix
          const base64String = e.target.result.split(",")[1];

          await customizeNotetakerBot({
            kind: "customizeImage",
            customBotImage: base64String,
            customBotImageFileName: file.name,
          });

          showToast({ type: "success", title: "Saved" });
          errorText = "";
          await teamStore.get();
        } catch (uploadError) {
          if (uploadError instanceof ToastError) {
            errorText = uploadError.subtitle;
            showToast(uploadError);
          } else {
            errorText = "Failed to upload image. Please try again.";
            showToast({
              type: "danger",
              title: "Upload failed",
              subtitle: errorText,
            });
          }
        } finally {
          uploading = false;
          clearFileInput();
        }
      };

      fileReader.onerror = () => {
        errorText = "Failed to read file. Please try again.";
        uploading = false;
        clearFileInput();
      };

      fileReader.readAsDataURL(file);
    } catch {
      errorText = "An unexpected error occurred.";
      showToast({
        type: "danger",
        title: "Upload failed",
        subtitle: errorText,
      });
      uploading = false;
      clearFileInput();
    }
  };

  const onclick = () => {
    if (fileInput) {
      fileInput.click();
    }
  };

  const onFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const { valid, errorMsg } = validateFile(file);
    if (!valid) {
      errorText = errorMsg;
      showToast({
        type: "danger",
        title: "Invalid file",
        subtitle: errorMsg,
      });
      clearFileInput();
      return;
    }

    handleFileUpload(file);
  };

  $: notificationProps = errorText
    ? { message: "error", style: "padding: 0;", contrast: "low" }
    : {
        message: "informationSubtle",
        contrast: "low",
        style: "color: var(--text-grey); padding: 0;",
      };

  $: notificationText =
    errorText ||
    "JPG only, max size: 2MB, ratio: 16:9, recommended resolution: 320x180.";
</script>

<div class="custom-image-wrapper">
  <input
    bind:this={fileInput}
    type="file"
    accept=".jpg,.jpeg,image/jpeg"
    on:change={onFileChange}
    class="file-input"
  />
  <div class="custom-image-setting">
    <div class="image-wrapper">
      {#if uploading}
        <div class="image skeleton"></div>
      {:else}
        <div class="image">
          {#if imageUrl}
            <img src={imageUrl} alt="Bot Avatar" />
          {:else}
            <Icon class="far fa-upload" size="small" />
          {/if}
        </div>
      {/if}
      <div class="image-info">
        <span class="image-name">{fileName}</span>
        {#if updatedText}
          <span class="image-uploaded">{updatedText}</span>
        {/if}
      </div>
    </div>
    <Button kind="secondary" size="large" style="width: 110px" {onclick}>
      Change
    </Button>
  </div>
  <InlineNotification {...notificationProps}>
    {notificationText}
  </InlineNotification>
</div>
